const USER_AUTH_CODES = [
  "auth/cancelled-popup-request",
  "auth/code-expired",
  "auth/credential-already-in-use",
  "auth/email-already-in-use",
  "auth/expired-action-code",
  "auth/invalid-action-code",
  "auth/invalid-credential",
  "auth/invalid-email",
  "auth/invalid-multi-factor-session",
  "auth/invalid-phone-number",
  "auth/invalid-verification-code",
  "auth/invalid-verification-id",
  "auth/missing-phone-number",
  "auth/missing-verification-code",
  "auth/missing-verification-id",
  "auth/network-request-failed",
  "auth/popup-closed-by-user",
  "auth/redirect-cancelled-by-user",
  "auth/requires-recent-login",
  "auth/second-factor-already-in-use",
  "auth/timeout",
  "auth/too-many-requests",
  "auth/user-cancelled",
  "auth/user-not-found",
  "auth/weak-password",
  "auth/wrong-password",
] as const;

const userAuthCodes = new Set<string>(USER_AUTH_CODES);

const userAuthCodePattern = new RegExp(
  `(?:${[...USER_AUTH_CODES].sort((left, right) => right.length - left.length).join("|")})(?![\\w-])`,
);

const userMessagePattern =
  /reCAPTCHA Timeout|Database deleted by request of the user|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Got error (?:wrongpass|access_denied) from query in Powens Return|client is offline/;

export const isExpectedUserErrorText = (text: string) =>
  userAuthCodePattern.test(text) || userMessagePattern.test(text);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const matchesExpectedUserError = (value: unknown) => {
  if (typeof value === "string") return isExpectedUserErrorText(value);
  if (!isRecord(value)) return false;
  if (typeof value.code === "string" && userAuthCodes.has(value.code)) return true;
  return typeof value.message === "string" && isExpectedUserErrorText(value.message);
};

export const isExpectedUserError = (error: unknown) => {
  const seen = new Set<unknown>();
  let current = error;
  while (current !== undefined && current !== null && !seen.has(current)) {
    if (matchesExpectedUserError(current)) return true;
    seen.add(current);
    if (!isRecord(current) || !("cause" in current)) return false;
    current = current.cause;
  }
  return false;
};

export const isExpectedUserSentryEvent = (event: unknown) => {
  if (!isRecord(event)) return false;
  if (typeof event.message === "string" && isExpectedUserErrorText(event.message)) return true;
  if (!isRecord(event.exception) || !Array.isArray(event.exception.values)) return false;
  return event.exception.values.some(
    (exception) =>
      isRecord(exception) &&
      typeof exception.value === "string" &&
      isExpectedUserErrorText(exception.value),
  );
};
